import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/orbit/context/AuthContext";
import { crmApi, formatApiError } from "@/orbit/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function AcceptInvite() {
  const { token } = useParams();
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await crmApi.get(`/auth/invite/${token}`);
        setInvite(data);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || "Invalid invitation");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await acceptInvite({ token, password, first_name: firstName, last_name: lastName });
      toast.success("Welcome to the team!");
      navigate("/orbit/app/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not accept invite");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8" data-testid="orbit-accept-page">
      <div className="w-full max-w-sm">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#1E40AF] font-semibold">Orbit CRM</div>

        {loading && <div className="mt-8 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Verifying invitation…</div>}

        {!loading && !invite && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-700 rounded-md p-4 text-sm" data-testid="orbit-accept-invalid">
            {error || "This invitation is invalid or has expired."}
          </div>
        )}

        {!loading && invite && (
          <>
            <h2 className="mt-3 text-2xl font-semibold text-[#0A1F44]">Join {invite.workspace_name}</h2>
            <p className="mt-2 text-sm text-slate-500">
              Invited by {invite.inviter_name} as <span className="font-medium capitalize">{invite.role.replace("_", " ")}</span>.
              Signing in as <strong>{invite.email}</strong>.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="orbit-accept-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">First name</label>
                  <input type="text" required value={firstName} onChange={(e) => setFirstName(e.target.value)}
                    data-testid="orbit-accept-first-name"
                    className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40 focus:border-[#1E40AF]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Last name</label>
                  <input type="text" required value={lastName} onChange={(e) => setLastName(e.target.value)}
                    data-testid="orbit-accept-last-name"
                    className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40 focus:border-[#1E40AF]" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Password</label>
                <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="orbit-accept-password"
                  className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40 focus:border-[#1E40AF]" />
              </div>
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">{error}</div>}
              <button type="submit" disabled={submitting} data-testid="orbit-accept-submit"
                className="w-full flex items-center justify-center gap-2 bg-[#0A1F44] text-white py-2.5 rounded-md text-sm font-semibold hover:bg-[#1E40AF] transition disabled:opacity-60">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Accept & sign in
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
